import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, Subject, defer } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { SearchResult } from './../../models/searchresult.interface';
import { APP_CONFIG } from 'src/app/app.config';
import { ApolloSearchService } from './apollo/apollo-search.service';
import { APOLLO_FETCH_ERROR } from '../../shared/constants/error.constants';

type PageContent = {
    url: string;
    title: string;
    text: string;
    slug: string;
    parentTitle: string;
    parentUrl: string;
    isTabbed: boolean;
    childType: string;
};

type JsonSearchPayload =
    | PageContent[]
    | {
          version?: string;
          pages?: PageContent[];
      };

@Injectable({
    providedIn: 'root',
})
export class SearchService {
    public readonly searching$ = new BehaviorSubject<boolean>(false);
    public readonly error$ = new BehaviorSubject<string | null>(null);
    public results = new Subject<SearchResult>();

    private readonly appConfig = inject(APP_CONFIG);
    private readonly http = inject(HttpClient);
    private readonly apolloSearchService = inject(ApolloSearchService);

    private stripMediaTags(html: string): string {
        let cleaned = html;
        cleaned = cleaned.replaceAll(
            /<picture\b[^>]*>[\s\S]*?<\/picture>/gi,
            ' ',
        );
        cleaned = cleaned.replaceAll(/<img\b[^>]*>/gi, ' ');
        cleaned = cleaned.replaceAll(/<source\b[^>]*>/gi, ' ');
        return cleaned;
    }

    private toSearchableText(content: string | null | undefined): string {
        if (!content) return '';
        const spn = document.createElement('span');
        spn.innerHTML = this.stripMediaTags(content);
        let text = (spn.textContent ?? '').toLowerCase();
        text = text.replaceAll(/(\r\n|\n|\r)/gm, ' ');
        text = text.replaceAll(/\s\s+/g, ' ');
        return text;
    }

    private unwrapJsonSearchPayload(payload: JsonSearchPayload): PageContent[] {
        if (Array.isArray(payload)) return payload;
        return payload?.pages ?? [];
    }

    getPages$(): Observable<PageContent[]> {
        if (this.appConfig.useJson) {
            const searchUrl = `${this.appConfig.assetsPath}${this.appConfig.json.search}`;
            return this.http.get<JsonSearchPayload>(searchUrl).pipe(
                map((payload) => this.unwrapJsonSearchPayload(payload)),
                map((pages) =>
                    (pages ?? []).map((page) => ({
                        ...page,
                        text: this.toSearchableText(page?.text),
                    })),
                ),
                catchError((err) => {
                    console.error(`Error: ${err}`);
                    this.error$.next(APOLLO_FETCH_ERROR);
                    return EMPTY;
                }),
            );
        }

        return this.apolloSearchService.fetchSearchPages$().pipe(
            map((nodes) =>
                (nodes ?? []).map((page: any) => {
                    return {
                        url: page?.uri,
                        title: page?.title,
                        text: this.toSearchableText(page?.content),
                        slug: page?.slug,
                        parentTitle: page?.parent?.node?.title,
                        parentUrl: page?.parent?.node?.uri,
                        isTabbed:
                            page?.parent?.node?.adsHierarchy?.childPages ===
                            'tabs',
                        childType: page?.adsHierarchy?.childPages,
                    };
                }),
            ),
            catchError((err) => {
                console.error(`Error: ${err}`);
                this.error$.next(APOLLO_FETCH_ERROR);
                return EMPTY;
            }),
        );
    }

    searchContent(search: string, tag: any): void {
        defer(() => {
            this.searching$.next(true);
            this.error$.next(null);
            return this.getPages$();
        })
            .pipe(
                tap((data) => {
                    (data ?? []).forEach((page: any) => {
                        const sr = new SearchResult(search, page, tag);
                        if (sr.occurances > 0) {
                            this.results.next(sr);
                        }
                    });
                }),
                finalize(() => this.searching$.next(false)),
            )
            .subscribe();
    }
}
